import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { UploadTypes } from 'src/app/modules/shared/enums/uploadTypes';
import { UnsupportedFormatError } from 'src/app/modules/shared/errors/file-errors';
import { CorsBlockedError, InvalidURLError, NetworkError } from 'src/app/modules/shared/errors/network-errors';
import { AlertService } from 'src/app/modules/shared/services/alert.service';
import { DocumentService } from 'src/app/modules/shared/services/document.service';
import { UrlService } from 'src/app/modules/shared/services/url.service';
import { ImageUploadResult } from 'src/app/modules/shared/types/imageUploadResult.type';
import { ImageUtilities } from 'src/app/modules/shared/utilities/image.utilities';
import { T } from 'src/assets/i18n/translation-keys';

@Component({
    selector: 'app-image-upload',
    templateUrl: './image-upload.component.html',
    styleUrl: './image-upload.component.scss',
    standalone: false
})
export class ImageUploadComponent implements OnInit, OnChanges {
  @Input() showLinkOption: boolean = true;
  @Input() showHelperText: boolean = true;
  @Input() showLabel: boolean = true;
  @Input() customButtonText: string = '';
  @Input() handleFileImageUploadInternally: boolean = true;
  @Input() handleURLImageUploadInternally: boolean = false;
  @Input() initialImageFile: File | null = null;
  @Input() initialImageLink: string = '';
  @Input() initialUploadType: UploadTypes | null = null;
  @Input() initialImageDimensions: { width: number; height: number } | null = null;
  @Input() enableStateOutput: boolean = false;
  @Input() allowedTypes?: string[];
  @Input() showFileTypeInfo: boolean = false;
  @Output() imageUploaded = new EventEmitter<ImageUploadResult>();
  @Output() imageFileAdded = new EventEmitter<ImageUploadResult>();
  @Output() imageUrlAdded = new EventEmitter<ImageUploadResult>();
  @Output() imageRemoved = new EventEmitter<void>();
  @Output() imageStateChanged = new EventEmitter<{
    imageFile: File | null;
    imageLink: string;
    imageDimensions: { width: number; height: number } | null;
    uploadType: UploadTypes;
  }>();

  public readonly T = T;
  public loading = false;

  public uploadTypes = UploadTypes;
  public uploadType = UploadTypes.File;

  public imageLink: string = '';
  public imageFile: File | null = null;
  public imageDimensions: { width: number; height: number } | null = null;

  public documentDetailsForm: UntypedFormGroup = new UntypedFormGroup({
    link: new UntypedFormControl('', [
      Validators.required,
      Validators.pattern("^(?:http(s)?://)?[\\w.-]+(?:\\.[\\w\\.-]+)+[\\w\\-\\._~:/?#[\\]@!\\$&'\\(\\)\\*\\+,;=.]+$"),
    ]),
    title: new UntypedFormControl('', []),
    description: new UntypedFormControl('', []),
  });

  constructor(
    public changeDetectorRef: ChangeDetectorRef,
    private readonly documentsService: DocumentService,
    private readonly urlService: UrlService,
    private readonly alertService: AlertService,
    private readonly translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.initializeFromInputs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialImageFile'] || changes['initialImageLink'] ||
        changes['initialUploadType'] || changes['initialImageDimensions']) {
      this.initializeFromInputs();
    }
  }

  private initializeFromInputs(): void {
    if (this.initialImageFile) {
      this.imageFile = this.initialImageFile;
    }
    if (this.initialImageLink) {
      this.imageLink = this.initialImageLink;
    }
    if (this.initialUploadType) {
      this.uploadType = this.initialUploadType;
    }
    if (this.initialImageDimensions) {
      this.imageDimensions = this.initialImageDimensions;
    }

    this.changeDetectorRef.detectChanges();
  }

  private emitImageState(): void {
    if (this.enableStateOutput) {
      this.imageStateChanged.emit({
        imageFile: this.imageFile,
        imageLink: this.imageLink,
        imageDimensions: this.imageDimensions,
        uploadType: this.uploadType
      });
    }
  }

  public onFileAdded(files: File[]): void {
    if(!files || files.length === 0) {
      return;
    }

    const file = files[0];
    if (!this.isValidImageFile(file)) {
      return;
    }

    this.imageFile = file;
    this.loadImagePreviewFromFile(file);
    this.getImageDimensions(file);
    this.emitImageState();
  }

  public onAddLinkClicked() {
    this.uploadType = UploadTypes.Hyperlink;
    this.changeDetectorRef.detectChanges();
  }

  public onLinkCancel() {
    this.uploadType = UploadTypes.File;
    this.changeDetectorRef.detectChanges();
  }

  public async onLinkConfirm() {
    if (this.documentDetailsForm.invalid) {
      return;
    }

    const link = this.documentDetailsForm.get('link')?.value;
    await this.loadImageFromUrl(link);
  }

  public removeImage() {
    this.imageLink = '';
    this.imageFile = null;
    this.imageDimensions = null;
    this.changeDetectorRef.detectChanges();

    this.imageRemoved.emit();
    this.emitImageState();
  }

  public truncateFileName(fileName: string, maxLength: number): string {
    if (fileName.length <= maxLength) return fileName;
    const extension = fileName.split('.').pop();
    const nameWithoutExtension = fileName.slice(0, fileName.lastIndexOf('.'));
    const truncatedName = nameWithoutExtension.slice(0, maxLength - extension!.length - 4) + '...';
    return `${truncatedName}.${extension}`;
  }

  public getFileTypeDisplay(mimeType: string): string {
    const typeMap: { [key: string]: string } = {
      'image/jpeg': 'JPEG',
      'image/jpg': 'JPG',
      'image/png': 'PNG',
      'image/gif': 'GIF',
      'image/webp': 'WebP',
      'image/svg+xml': 'SVG'
    };
    return typeMap[mimeType] || mimeType.split('/')[1]?.toUpperCase() || 'Unknown';
  }

  public formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Loads and displays an image preview from a selected file.
   *
   * This method reads the file using FileReader, updates the image preview link,
   * and optionally handles the file upload internally based on the component configuration.
   * Updates the uploading state and triggers change detection throughout the process.
   *
   * @param file - The image file to load and preview
   * @returns void
   */
  private loadImagePreviewFromFile(file: File): void {
    this.loading = true;

    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.imageLink = e.target.result;
      this.changeDetectorRef.detectChanges();

      if(this.handleFileImageUploadInternally) {
        this.handleFileUpload(file);
      }
      this.loading = false;
      this.changeDetectorRef.detectChanges();
    };
    reader.readAsDataURL(file);
  }

  private getImageDimensions(file: File): void {
    const img = new Image();
    img.onload = () => {
      this.imageDimensions = {
        width: img.naturalWidth,
        height: img.naturalHeight
      };
      this.changeDetectorRef.detectChanges();
    };
    img.src = URL.createObjectURL(file);
  }

  private isValidImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  /**
   * Handles the file upload process for an image file.
   *
   * @param file - The File object to be uploaded
   *
   * @remarks
   * This method performs the following operations:
   * - Sets the uploading state to true
   * - Calls the documents service to split and upload the file
   * - On success: builds a resource URL and emits the upload result
   * - On error: removes the image
   * - On completion: resets the uploading state and triggers change detection
   *
   * @emits imageUploaded - Emitted when upload is successful with file details
   * @returns void
   */
  private handleFileUpload(file: File) {
    this.loading = true;
    this.documentsService.splitUploadFile(file, false, false, false).subscribe({
      next: (res) => {
        const fileName = res;
        const url = this.urlService.buildResourceUrl(`/Resource/library/data/${fileName}`);

        this.imageUploaded.emit({
          fileName,
          url,
          uploadType: UploadTypes.File
        });
      },
      error: (_err) => {
        this.removeImage();
      },
      complete: () => {
        this.loading = false;
        this.changeDetectorRef.detectChanges();
      }
    });
  }


  /**
   * Loads an image from a URL and processes it for upload.
   *
   * @param imageUrl - The URL of the image to load
   * @returns A Promise that resolves when the image has been loaded and processed
   *
   * @remarks
   * This method fetches image information from the provided URL, sets the component's
   * image properties, emits an imageUrlAdded event, and optionally handles internal
   * file upload if configured to do so.
   *
   * The loading state is managed throughout the process and change detection is
   * triggered upon completion.
   *
   * @throws Will silently catch and ignore any errors that occur during image loading
   */
  private async loadImageFromUrl(imageUrl: string): Promise<void> {
    try {
      this.loading = true;
      this.changeDetectorRef.detectChanges();

      const imageInfo = await ImageUtilities.fetchImageWithInfo(imageUrl);
      this.imageFile = imageInfo.file;
      this.imageLink = imageUrl;
      this.imageDimensions = {
        width: imageInfo.width,
        height: imageInfo.height
      };

      this.imageUrlAdded.emit({
        fileName: imageInfo.file.name,
        url: imageUrl,
        uploadType: UploadTypes.Hyperlink
      });

      if(this.handleURLImageUploadInternally) {
        this.handleFileUpload(imageInfo.file);
      }

      this.emitImageState();
    } catch (error: unknown) {
      if (error instanceof CorsBlockedError) {
        void this.alertService.error(this.translateService.instant(T.error.image_fetch_cors_error));
      } else if(error instanceof InvalidURLError) {
        void this.alertService.error(this.translateService.instant(T.common.invalid_url));
      } else if(error instanceof NetworkError) {
        const errorStatus = error.status;
        if (errorStatus === 404) {
          void this.alertService.error(this.translateService.instant(T.error.network_error_not_found));
        } else {
          void this.alertService.error(this.translateService.instant(T.error.network_error_while_fetching_image));
        }
      } else if (error instanceof UnsupportedFormatError) {
        void this.alertService.error(this.translateService.instant(T.common.unsupported_file_type));
      } else {
        void this.alertService.error(this.translateService.instant(T.error.unexpected_error_loading_image));
      }
    } finally {
      this.loading = false;
      this.changeDetectorRef.detectChanges();
    }
  }
}
